import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { DemoStorageService } from '../../core/demo-storage.service';
import { ConfiguracionService } from '../configuracion/configuracion.service';
import type {
  EntregaEPP,
  AreaEPP,
  TrabajadorEPP,
  EstadoEntregaEPP,
} from './models/epp.model';
import { getEstadoEntrega } from './models/epp.model';
import type { TipoEPPConfig } from '../configuracion/models/configuracion.model';

const AREAS: AreaEPP[] = [
  { id: '1', nombre: 'Producción' },
  { id: '2', nombre: 'Mantenimiento' },
  { id: '3', nombre: 'Almacén' },
  { id: '4', nombre: 'Oficinas' },
  { id: '5', nombre: 'Laboratorio' },
];

const TRABAJADORES: TrabajadorEPP[] = [
  { id: 'T1', nombre: 'Juan Pérez', areaId: '1' },
  { id: 'T2', nombre: 'Ana Martínez', areaId: '5' },
  { id: 'T3', nombre: 'Luis Fernández', areaId: '2' },
  { id: 'T4', nombre: 'Carmen Soto', areaId: '1' },
  { id: 'T5', nombre: 'Pedro Sánchez', areaId: '3' },
  { id: 'T6', nombre: 'Rosa Díaz', areaId: '4' },
  { id: 'T7', nombre: 'Miguel Torres', areaId: '2' },
  { id: 'T8', nombre: 'Laura Gómez', areaId: '5' },
];

function demoEntregas(): EntregaEPP[] {
  const d = (daysAgo: number) =>
    new Date(Date.now() - daysAgo * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const next = (daysFromNow: number) =>
    new Date(Date.now() + daysFromNow * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
  return [
    { id: '1', trabajadorId: 'T1', tipoEPP: 'casco', fechaEntrega: d(90), fechaVencimiento: next(275), cantidad: 1, observaciones: '', devuelto: false, fechaDevolucion: '', createdAt: now, updatedAt: now },
    { id: '2', trabajadorId: 'T1', tipoEPP: 'guantes', fechaEntrega: d(20), fechaVencimiento: next(345), cantidad: 3, observaciones: 'Talla L', devuelto: false, fechaDevolucion: '', createdAt: now, updatedAt: now },
    { id: '3', trabajadorId: 'T2', tipoEPP: 'gafas', fechaEntrega: d(60), fechaVencimiento: next(305), cantidad: 1, observaciones: 'Antiempañante', devuelto: false, fechaDevolucion: '', createdAt: now, updatedAt: now },
    { id: '4', trabajadorId: 'T2', tipoEPP: 'tapabocas', fechaEntrega: d(15), fechaVencimiento: next(15), cantidad: 10, observaciones: 'N95. Renovar en 30 días', devuelto: false, fechaDevolucion: '', createdAt: now, updatedAt: now },
    { id: '5', trabajadorId: 'T3', tipoEPP: 'arnes', fechaEntrega: d(200), fechaVencimiento: d(10), cantidad: 1, observaciones: 'Trabajo en alturas. Vencido, pendiente recambio', devuelto: false, fechaDevolucion: '', createdAt: now, updatedAt: now },
    { id: '6', trabajadorId: 'T3', tipoEPP: 'casco', fechaEntrega: d(200), fechaVencimiento: next(165), cantidad: 1, observaciones: '', devuelto: false, fechaDevolucion: '', createdAt: now, updatedAt: now },
    { id: '7', trabajadorId: 'T4', tipoEPP: 'calzado', fechaEntrega: d(120), fechaVencimiento: next(245), cantidad: 1, observaciones: 'Talla 38', devuelto: false, fechaDevolucion: '', createdAt: now, updatedAt: now },
    { id: '8', trabajadorId: 'T5', tipoEPP: 'guantes', fechaEntrega: d(30), fechaVencimiento: next(25), cantidad: 2, observaciones: 'Carga. Por vencer', devuelto: false, fechaDevolucion: '', createdAt: now, updatedAt: now },
    { id: '9', trabajadorId: 'T6', tipoEPP: 'gafas', fechaEntrega: d(300), fechaVencimiento: d(65), cantidad: 1, observaciones: 'Devuelto por cambio de puesto', devuelto: true, fechaDevolucion: d(5), createdAt: now, updatedAt: now },
    { id: '10', trabajadorId: 'T7', tipoEPP: 'auditivos', fechaEntrega: d(45), fechaVencimiento: next(320), cantidad: 1, observaciones: 'Orejeras', devuelto: false, fechaDevolucion: '', createdAt: now, updatedAt: now },
    { id: '11', trabajadorId: 'T8', tipoEPP: 'protector_facial', fechaEntrega: d(10), fechaVencimiento: next(355), cantidad: 1, observaciones: 'Laboratorio', devuelto: false, fechaDevolucion: '', createdAt: now, updatedAt: now },
    { id: '12', trabajadorId: 'T8', tipoEPP: 'guantes', fechaEntrega: d(10), fechaVencimiento: next(355), cantidad: 4, observaciones: 'Nitrilo', devuelto: false, fechaDevolucion: '', createdAt: now, updatedAt: now },
  ];
}

const STORAGE_KEY = 'epp_entregas';

@Injectable({ providedIn: 'root' })
export class EppService {
  private items: EntregaEPP[] = demoEntregas();

  constructor(
    private config: ConfiguracionService,
    private demoStorage: DemoStorageService
  ) {}

  async loadFromDemoStorage(): Promise<void> {
    if (!environment.demo) return;
    const data = await this.demoStorage.get<EntregaEPP[]>(STORAGE_KEY);
    this.items = Array.isArray(data) ? data : demoEntregas();
    await this.persistDemo();
  }

  private persistDemo(): Promise<void> {
    if (!environment.demo) return Promise.resolve();
    return this.demoStorage.set(STORAGE_KEY, this.items).catch(() => {});
  }

  getTiposEpp(): TipoEPPConfig[] {
    return this.config.getTiposEpp();
  }

  getAreas(): AreaEPP[] {
    return [...AREAS];
  }

  getTrabajadores(): TrabajadorEPP[] {
    return [...TRABAJADORES];
  }

  getTrabajadorById(id: string): TrabajadorEPP | null {
    return TRABAJADORES.find((t) => t.id === id) ?? null;
  }

  getList(filters?: {
    busqueda?: string;
    areaId?: string;
    trabajadorId?: string;
    tipoEPP?: string;
    estado?: EstadoEntregaEPP;
  }): EntregaEPP[] {
    let list = [...this.items];
    if (filters?.busqueda?.trim()) {
      const q = filters.busqueda.trim().toLowerCase();
      list = list.filter((e) => {
        const t = this.getTrabajadorById(e.trabajadorId);
        const areaName = t ? AREAS.find((a) => a.id === t.areaId)?.nombre ?? '' : '';
        return (
          (t?.nombre.toLowerCase().includes(q)) ||
          areaName.toLowerCase().includes(q) ||
          e.observaciones.toLowerCase().includes(q)
        );
      });
    }
    if (filters?.areaId) {
      const trabajadorIdsInArea = TRABAJADORES.filter((t) => t.areaId === filters.areaId).map((t) => t.id);
      list = list.filter((e) => trabajadorIdsInArea.includes(e.trabajadorId));
    }
    if (filters?.trabajadorId) list = list.filter((e) => e.trabajadorId === filters.trabajadorId);
    if (filters?.tipoEPP) list = list.filter((e) => e.tipoEPP === filters.tipoEPP);
    if (filters?.estado) {
      list = list.filter((e) => getEstadoEntrega(e) === filters.estado);
    }
    return list.sort((a, b) => b.fechaEntrega.localeCompare(a.fechaEntrega));
  }

  getById(id: string): EntregaEPP | null {
    return this.items.find((e) => e.id === id) ?? null;
  }

  /** Entregas agrupadas por trabajador (para vista por trabajador). */
  getEntregasPorTrabajador(filters?: { areaId?: string; trabajadorId?: string }): Map<string, EntregaEPP[]> {
    const list = this.getList({ areaId: filters?.areaId, trabajadorId: filters?.trabajadorId });
    const map = new Map<string, EntregaEPP[]>();
    for (const e of list) {
      const arr = map.get(e.trabajadorId) ?? [];
      arr.push(e);
      map.set(e.trabajadorId, arr);
    }
    return map;
  }

  getResumenAlertas(): { vencidos: number; porVencer: number } {
    let vencidos = 0;
    let porVencer = 0;
    for (const e of this.items) {
      const est = getEstadoEntrega(e);
      if (est === 'vencido') vencidos++;
      else if (est === 'por_vencer') porVencer++;
    }
    return { vencidos, porVencer };
  }

  create(dto: Omit<EntregaEPP, 'id' | 'createdAt' | 'updatedAt'>): EntregaEPP {
    const id = String(Date.now());
    const now = new Date().toISOString().slice(0, 19).replace('T', ' ');
    const nuevo: EntregaEPP = {
      ...dto,
      id,
      createdAt: now,
      updatedAt: now,
    };
    this.items.push(nuevo);
    this.persistDemo();
    return nuevo;
  }

  update(id: string, dto: Partial<Omit<EntregaEPP, 'id' | 'createdAt'>>): EntregaEPP | null {
    const idx = this.items.findIndex((e) => e.id === id);
    if (idx === -1) return null;
    const updated: EntregaEPP = {
      ...this.items[idx],
      ...dto,
      updatedAt: new Date().toISOString().slice(0, 19).replace('T', ' '),
    };
    this.items[idx] = updated;
    this.persistDemo();
    return updated;
  }

  delete(id: string): boolean {
    const idx = this.items.findIndex((e) => e.id === id);
    if (idx === -1) return false;
    this.items.splice(idx, 1);
    this.persistDemo();
    return true;
  }
}
